// Web CRM (Business plane) Dashboard i18n dictionary — STATIC UI CHROME ONLY.
// Merged into the flat per-language lookup in ./translations.js (same t() semantics).
// Covers the OverviewDashboard, BusinessDashboard loading states, FeatureGate read-only
// notices, and the BusinessSidebar menu-item descriptions (navdesc.*).
//
// SCOPE: static labels only. NEVER used for dynamic/backend/user content. English is the
// default + fallback. Every language MUST contain the same key set.

const dashboardTranslations = {
  en: {
    // Role-based greetings
    'dash.greeting.admin': 'System Overview & Management',
    'dash.greeting.operator': 'Customer Service Dashboard',
    'dash.greeting.manager': 'Team Performance Overview',
    'dash.greeting.analyst': 'Data Analytics Dashboard',
    'dash.greeting.director': 'Strategic Insights Dashboard',
    'dash.greeting.executive': 'Executive Summary Dashboard',
    'dash.greeting.it': 'Technical Operations Dashboard',
    'dash.greeting.super_admin': 'Global Control Tower',
    'dash.greeting.default': 'Dashboard Overview',
    'dash.welcome': 'Welcome back, {name}!',
    'dash.brandId': 'Brand ID:',
    // Stat cards
    'dash.stat.totalTickets': 'Total Tickets',
    'dash.stat.resolved': 'Resolved',
    'dash.stat.activeEscalations': 'Active Escalations',
    'dash.stat.conversations': 'Conversations',
    'dash.stat.teamMembers': 'Team Members',
    // Take-Call dialog
    'dash.callQueue.title': 'Incoming Call Queue',
    'dash.callQueue.subtitle': 'Live escalation queue for {business}',
    'dash.callQueue.checking': 'Checking queue...',
    'dash.callQueue.live': 'Live',
    'dash.callQueue.callsWaiting': '{count} in queue',
    'dash.callQueue.customer': 'Customer:',
    'dash.callQueue.reason': 'Reason:',
    'dash.callQueue.status': 'Status:',
    'dash.callQueue.dismiss': 'Dismiss',
    'dash.callQueue.acceptCall': 'Accept Call',
    'dash.callQueue.empty': 'No incoming calls in queue',
    'dash.callQueue.emptyDetail': 'Queue is currently empty.',
    'dash.callQueue.openDesktop': 'Open Agent Desktop',
    // New-Ticket dialog
    'dash.newTicket.title': 'Create New Ticket',
    'dash.newTicket.subtitle': 'Create a support ticket for a customer',
    'dash.newTicket.customer': 'Customer *',
    'dash.newTicket.customerPlaceholder': 'Customer name or email',
    'dash.newTicket.issueTitle': 'Issue Title *',
    'dash.newTicket.issueTitlePlaceholder': 'Brief description of the issue',
    'dash.newTicket.category': 'Category',
    'dash.newTicket.selectCategory': 'Select category',
    'dash.newTicket.catTechnical': 'Technical Support',
    'dash.newTicket.catBilling': 'Billing',
    'dash.newTicket.catGeneral': 'General Inquiry',
    'dash.newTicket.catAccount': 'Account Management',
    'dash.newTicket.catProduct': 'Product Support',
    'dash.newTicket.priority': 'Priority',
    'dash.newTicket.priorityLow': 'Low',
    'dash.newTicket.priorityMedium': 'Medium',
    'dash.newTicket.priorityHigh': 'High',
    'dash.newTicket.priorityUrgent': 'Urgent',
    'dash.newTicket.description': 'Description *',
    'dash.newTicket.descriptionPlaceholder': 'Detailed description of the issue',
    'dash.newTicket.create': 'Create Ticket',
    // Ticket-create toasts
    'dash.toast.missingInfo': 'Missing Information',
    'dash.toast.missingInfoDetail': 'Please fill in all required fields',
    'dash.toast.ticketCreated': 'Ticket Created',
    'dash.toast.ticketCreatedDetail': 'Support ticket created successfully',
    'dash.toast.ticketError': 'Failed to create ticket. Please try again.',
    // Stat card sample-data / live badges
    'dash.sampleData': 'Sample Data',
    'dash.notConnectedShort': 'Not yet connected to live source.',
    'dash.notConnectedLong': 'This metric is not yet connected to a live data source.',
    'dash.live': 'LIVE',
    // Recent Activity
    'dash.recentActivity': 'Recent Activity',
    'dash.loadingActivity': 'Loading activity...',
    'dash.noActivity': 'No recent activity yet.',
    // Performance Metrics
    'dash.performanceMetrics': 'Performance Metrics',
    'dash.loadingMetrics': 'Loading metrics...',
    'dash.autoResolutionRate': 'Auto-Resolution Rate',
    'dash.escalationRate': 'Escalation Rate',
    'dash.totalConversationsAnalyzed': 'Total AI conversations analyzed',
    'dash.noConversationData': 'No AI conversation data yet. Metrics will appear once customers use AI chat.',
    // System Health
    'dash.systemHealth': 'System Health',
    'dash.businessHealth': 'Business Health',
    'dash.healthy': 'Healthy',
    'dash.degraded': 'Degraded',
    'dash.unavailable': 'Unavailable',
    'dash.someServicesDegraded': 'Some services may be degraded',
    'dash.checkingServices': 'Checking services…',
    'dash.notConfigured': 'Not configured',
    'dash.lastChecked': 'Last checked:',
    // Loading states
    'dash.loadingDashboard': 'Loading Dashboard...',
    'dash.loadingBusinessInfo': 'Please wait while we load your business information.',
    'dash.loadingDashboardWait': 'Please wait while we load your dashboard.',
    'dash.loadingBusinessData': 'Loading Business Data...',
    // FeatureGate read-only
    'dash.readOnly.title': 'Account is in read-only mode',
    'dash.readOnly.desc': 'Reactivate your subscription to use {label}.',
    'dash.readOnly.tooltip': 'Read-only mode — reactivate to unlock',
    // Sidebar menu descriptions
    'navdesc.dashboardOverview': 'Dashboard overview',
    'navdesc.monitorAiVoice': 'Monitor AI voice interactions',
    'navdesc.testAiVoiceComplex': 'Test AI voice with complex scenarios',
    'navdesc.makeIntegratedCalls': 'Make integrated calls',
    'navdesc.manageContacts': 'Manage your contacts',
    'navdesc.browseProducts': 'Browse products and services',
    'navdesc.participateSurveys': 'Participate in surveys',
    'navdesc.collaborateTeam': 'Collaborate with team members',
    'navdesc.allInOneAgent': 'All-in-one agent interface',
    'navdesc.manageCustomerTickets': 'Manage customer tickets',
    'navdesc.handleEscalated': 'Handle escalated conversations',
    'navdesc.viewSolutions': 'View solutions',
    'navdesc.aiVoice30': 'AI voice with 30+ scenarios',
    'navdesc.aiChat30': 'AI chat with 30+ scenarios',
    'navdesc.customerManagement': 'Customer management',
    'navdesc.monitorTeamPerf': 'Monitor team performance',
    'navdesc.manageTeamRoles': 'Manage team members & roles',
    'navdesc.advancedTicketOps': 'Advanced ticket operations',
    'navdesc.approveReject': 'Approve or reject submitted solutions',
    'navdesc.manageFeedbackCats': 'Manage feedback categories',
    'navdesc.reportsInsights': 'Reports, insights & AI metrics',
    'navdesc.manageShowroom': 'Manage business showroom',
    'navdesc.createSurveys': 'Create customer surveys',
    'navdesc.unifiedDataAccess': 'Unified data access',
    'navdesc.aiPerfAnalytics': 'AI performance analytics',
    'navdesc.advancedReporting': 'Advanced reporting & AI metrics',
    'navdesc.teamResourceMgmt': 'Team & resource management',
    'navdesc.strategicInsights': 'Strategic insights',
    'navdesc.strategicAnalytics': 'Strategic analytics & AI metrics',
    'navdesc.cLevelInsights': 'C-level insights',
    'navdesc.revenueRoi': 'Revenue & ROI analysis',
    'navdesc.executiveReporting': 'Executive reporting & AI metrics',
    'navdesc.apiSystemMgmt': 'API & system management',
    'navdesc.perfSystemLogs': 'Performance & system logs',
    'navdesc.systemAnalytics': 'System analytics & AI metrics',
    'navdesc.systemConfig': 'System configuration',
    'navdesc.manageTeamMembers': 'Manage team members',
    'navdesc.planInvoices': 'Plan, invoices, upgrade',
    'navdesc.allTicketAccess': 'All ticket access',
    'navdesc.knowledgeBase': 'Knowledge base',
    'navdesc.customerOversight': 'Customer oversight',
    'navdesc.fullAnalytics': 'Full analytics & AI metrics',
  },
  es: {
    'dash.greeting.admin': 'Vista general y gestión del sistema',
    'dash.greeting.operator': 'Panel de atención al cliente',
    'dash.greeting.manager': 'Resumen del rendimiento del equipo',
    'dash.greeting.analyst': 'Panel de análisis de datos',
    'dash.greeting.director': 'Panel de perspectivas estratégicas',
    'dash.greeting.executive': 'Panel de resumen ejecutivo',
    'dash.greeting.it': 'Panel de operaciones técnicas',
    'dash.greeting.super_admin': 'Torre de control global',
    'dash.greeting.default': 'Resumen del panel',
    'dash.welcome': '¡Bienvenido de nuevo, {name}!',
    'dash.brandId': 'ID de marca:',
    'dash.stat.totalTickets': 'Total de tickets',
    'dash.stat.resolved': 'Resueltos',
    'dash.stat.activeEscalations': 'Escalaciones activas',
    'dash.stat.conversations': 'Conversaciones',
    'dash.stat.teamMembers': 'Miembros del equipo',
    'dash.callQueue.title': 'Cola de llamadas entrantes',
    'dash.callQueue.subtitle': 'Cola de escalaciones en vivo para {business}',
    'dash.callQueue.checking': 'Comprobando la cola...',
    'dash.callQueue.live': 'En vivo',
    'dash.callQueue.callsWaiting': '{count} en cola',
    'dash.callQueue.customer': 'Cliente:',
    'dash.callQueue.reason': 'Motivo:',
    'dash.callQueue.status': 'Estado:',
    'dash.callQueue.dismiss': 'Descartar',
    'dash.callQueue.acceptCall': 'Aceptar llamada',
    'dash.callQueue.empty': 'No hay llamadas entrantes en la cola',
    'dash.callQueue.emptyDetail': 'La cola está vacía en este momento.',
    'dash.callQueue.openDesktop': 'Abrir escritorio del agente',
    'dash.newTicket.title': 'Crear nuevo ticket',
    'dash.newTicket.subtitle': 'Cree un ticket de soporte para un cliente',
    'dash.newTicket.customer': 'Cliente *',
    'dash.newTicket.customerPlaceholder': 'Nombre o correo del cliente',
    'dash.newTicket.issueTitle': 'Título del problema *',
    'dash.newTicket.issueTitlePlaceholder': 'Breve descripción del problema',
    'dash.newTicket.category': 'Categoría',
    'dash.newTicket.selectCategory': 'Seleccionar categoría',
    'dash.newTicket.catTechnical': 'Soporte técnico',
    'dash.newTicket.catBilling': 'Facturación',
    'dash.newTicket.catGeneral': 'Consulta general',
    'dash.newTicket.catAccount': 'Gestión de cuenta',
    'dash.newTicket.catProduct': 'Soporte de producto',
    'dash.newTicket.priority': 'Prioridad',
    'dash.newTicket.priorityLow': 'Baja',
    'dash.newTicket.priorityMedium': 'Media',
    'dash.newTicket.priorityHigh': 'Alta',
    'dash.newTicket.priorityUrgent': 'Urgente',
    'dash.newTicket.description': 'Descripción *',
    'dash.newTicket.descriptionPlaceholder': 'Descripción detallada del problema',
    'dash.newTicket.create': 'Crear ticket',
    'dash.toast.missingInfo': 'Falta información',
    'dash.toast.missingInfoDetail': 'Complete todos los campos obligatorios',
    'dash.toast.ticketCreated': 'Ticket creado',
    'dash.toast.ticketCreatedDetail': 'Ticket de soporte creado correctamente',
    'dash.toast.ticketError': 'No se pudo crear el ticket. Inténtelo de nuevo.',
    'dash.sampleData': 'Datos de muestra',
    'dash.notConnectedShort': 'Aún no conectado a una fuente en vivo.',
    'dash.notConnectedLong': 'Esta métrica aún no está conectada a una fuente de datos en vivo.',
    'dash.live': 'EN VIVO',
    'dash.recentActivity': 'Actividad reciente',
    'dash.loadingActivity': 'Cargando actividad...',
    'dash.noActivity': 'Aún no hay actividad reciente.',
    'dash.performanceMetrics': 'Métricas de rendimiento',
    'dash.loadingMetrics': 'Cargando métricas...',
    'dash.autoResolutionRate': 'Tasa de resolución automática',
    'dash.escalationRate': 'Tasa de escalación',
    'dash.totalConversationsAnalyzed': 'Total de conversaciones de IA analizadas',
    'dash.noConversationData': 'Aún no hay datos de conversaciones de IA. Las métricas aparecerán cuando los clientes usen el chat de IA.',
    'dash.systemHealth': 'Estado del sistema',
    'dash.businessHealth': 'Estado del negocio',
    'dash.healthy': 'Correcto',
    'dash.degraded': 'Degradado',
    'dash.unavailable': 'No disponible',
    'dash.someServicesDegraded': 'Algunos servicios pueden estar degradados',
    'dash.checkingServices': 'Comprobando servicios…',
    'dash.notConfigured': 'No configurado',
    'dash.lastChecked': 'Última comprobación:',
    'dash.loadingDashboard': 'Cargando el panel...',
    'dash.loadingBusinessInfo': 'Espere mientras cargamos la información de su empresa.',
    'dash.loadingDashboardWait': 'Espere mientras cargamos su panel.',
    'dash.loadingBusinessData': 'Cargando datos de la empresa...',
    'dash.readOnly.title': 'La cuenta está en modo de solo lectura',
    'dash.readOnly.desc': 'Reactive su suscripción para usar {label}.',
    'dash.readOnly.tooltip': 'Modo de solo lectura: reactive para desbloquear',
    'navdesc.dashboardOverview': 'Resumen del panel',
    'navdesc.monitorAiVoice': 'Supervisar interacciones de voz IA',
    'navdesc.testAiVoiceComplex': 'Probar la voz IA con escenarios complejos',
    'navdesc.makeIntegratedCalls': 'Realizar llamadas integradas',
    'navdesc.manageContacts': 'Gestionar sus contactos',
    'navdesc.browseProducts': 'Explorar productos y servicios',
    'navdesc.participateSurveys': 'Participar en encuestas',
    'navdesc.collaborateTeam': 'Colaborar con los miembros del equipo',
    'navdesc.allInOneAgent': 'Interfaz de agente todo en uno',
    'navdesc.manageCustomerTickets': 'Gestionar tickets de clientes',
    'navdesc.handleEscalated': 'Gestionar conversaciones escaladas',
    'navdesc.viewSolutions': 'Ver soluciones',
    'navdesc.aiVoice30': 'Voz IA con más de 30 escenarios',
    'navdesc.aiChat30': 'Chat IA con más de 30 escenarios',
    'navdesc.customerManagement': 'Gestión de clientes',
    'navdesc.monitorTeamPerf': 'Supervisar el rendimiento del equipo',
    'navdesc.manageTeamRoles': 'Gestionar miembros y roles del equipo',
    'navdesc.advancedTicketOps': 'Operaciones avanzadas de tickets',
    'navdesc.approveReject': 'Aprobar o rechazar soluciones enviadas',
    'navdesc.manageFeedbackCats': 'Gestionar categorías de comentarios',
    'navdesc.reportsInsights': 'Informes, perspectivas y métricas de IA',
    'navdesc.manageShowroom': 'Gestionar la sala de exposición del negocio',
    'navdesc.createSurveys': 'Crear encuestas para clientes',
    'navdesc.unifiedDataAccess': 'Acceso unificado a los datos',
    'navdesc.aiPerfAnalytics': 'Analíticas de rendimiento de IA',
    'navdesc.advancedReporting': 'Informes avanzados y métricas de IA',
    'navdesc.teamResourceMgmt': 'Gestión de equipos y recursos',
    'navdesc.strategicInsights': 'Perspectivas estratégicas',
    'navdesc.strategicAnalytics': 'Analíticas estratégicas y métricas de IA',
    'navdesc.cLevelInsights': 'Perspectivas para la alta dirección',
    'navdesc.revenueRoi': 'Análisis de ingresos y ROI',
    'navdesc.executiveReporting': 'Informes ejecutivos y métricas de IA',
    'navdesc.apiSystemMgmt': 'Gestión de API y sistemas',
    'navdesc.perfSystemLogs': 'Rendimiento y registros del sistema',
    'navdesc.systemAnalytics': 'Analíticas del sistema y métricas de IA',
    'navdesc.systemConfig': 'Configuración del sistema',
    'navdesc.manageTeamMembers': 'Gestionar miembros del equipo',
    'navdesc.planInvoices': 'Plan, facturas, mejora',
    'navdesc.allTicketAccess': 'Acceso a todos los tickets',
    'navdesc.knowledgeBase': 'Base de conocimiento',
    'navdesc.customerOversight': 'Supervisión de clientes',
    'navdesc.fullAnalytics': 'Analíticas completas y métricas de IA',
  },
  fr: {
    'dash.greeting.admin': 'Vue d’ensemble et gestion du système',
    'dash.greeting.operator': 'Tableau de bord du service client',
    'dash.greeting.manager': 'Aperçu des performances de l’équipe',
    'dash.greeting.analyst': 'Tableau de bord d’analyse de données',
    'dash.greeting.director': 'Tableau de bord des analyses stratégiques',
    'dash.greeting.executive': 'Tableau de bord du résumé exécutif',
    'dash.greeting.it': 'Tableau de bord des opérations techniques',
    'dash.greeting.super_admin': 'Tour de contrôle globale',
    'dash.greeting.default': 'Aperçu du tableau de bord',
    'dash.welcome': 'Bon retour, {name} !',
    'dash.brandId': 'ID de marque :',
    'dash.stat.totalTickets': 'Total des tickets',
    'dash.stat.resolved': 'Résolus',
    'dash.stat.activeEscalations': 'Escalades actives',
    'dash.stat.conversations': 'Conversations',
    'dash.stat.teamMembers': 'Membres de l’équipe',
    'dash.callQueue.title': 'File d’attente des appels entrants',
    'dash.callQueue.subtitle': 'File d’escalade en direct pour {business}',
    'dash.callQueue.checking': 'Vérification de la file...',
    'dash.callQueue.live': 'En direct',
    'dash.callQueue.callsWaiting': '{count} en file',
    'dash.callQueue.customer': 'Client :',
    'dash.callQueue.reason': 'Motif :',
    'dash.callQueue.status': 'Statut :',
    'dash.callQueue.dismiss': 'Ignorer',
    'dash.callQueue.acceptCall': 'Accepter l’appel',
    'dash.callQueue.empty': 'Aucun appel entrant dans la file',
    'dash.callQueue.emptyDetail': 'La file est actuellement vide.',
    'dash.callQueue.openDesktop': 'Ouvrir le bureau de l’agent',
    'dash.newTicket.title': 'Créer un nouveau ticket',
    'dash.newTicket.subtitle': 'Créez un ticket d’assistance pour un client',
    'dash.newTicket.customer': 'Client *',
    'dash.newTicket.customerPlaceholder': 'Nom ou e-mail du client',
    'dash.newTicket.issueTitle': 'Titre du problème *',
    'dash.newTicket.issueTitlePlaceholder': 'Brève description du problème',
    'dash.newTicket.category': 'Catégorie',
    'dash.newTicket.selectCategory': 'Sélectionner une catégorie',
    'dash.newTicket.catTechnical': 'Assistance technique',
    'dash.newTicket.catBilling': 'Facturation',
    'dash.newTicket.catGeneral': 'Demande générale',
    'dash.newTicket.catAccount': 'Gestion de compte',
    'dash.newTicket.catProduct': 'Assistance produit',
    'dash.newTicket.priority': 'Priorité',
    'dash.newTicket.priorityLow': 'Basse',
    'dash.newTicket.priorityMedium': 'Moyenne',
    'dash.newTicket.priorityHigh': 'Haute',
    'dash.newTicket.priorityUrgent': 'Urgente',
    'dash.newTicket.description': 'Description *',
    'dash.newTicket.descriptionPlaceholder': 'Description détaillée du problème',
    'dash.newTicket.create': 'Créer le ticket',
    'dash.toast.missingInfo': 'Informations manquantes',
    'dash.toast.missingInfoDetail': 'Veuillez remplir tous les champs obligatoires',
    'dash.toast.ticketCreated': 'Ticket créé',
    'dash.toast.ticketCreatedDetail': 'Ticket d’assistance créé avec succès',
    'dash.toast.ticketError': 'Échec de la création du ticket. Veuillez réessayer.',
    'dash.sampleData': 'Données d’exemple',
    'dash.notConnectedShort': 'Pas encore connecté à une source en direct.',
    'dash.notConnectedLong': 'Cette mesure n’est pas encore connectée à une source de données en direct.',
    'dash.live': 'EN DIRECT',
    'dash.recentActivity': 'Activité récente',
    'dash.loadingActivity': 'Chargement de l’activité...',
    'dash.noActivity': 'Aucune activité récente pour le moment.',
    'dash.performanceMetrics': 'Indicateurs de performance',
    'dash.loadingMetrics': 'Chargement des indicateurs...',
    'dash.autoResolutionRate': 'Taux de résolution automatique',
    'dash.escalationRate': 'Taux d’escalade',
    'dash.totalConversationsAnalyzed': 'Total des conversations IA analysées',
    'dash.noConversationData': 'Aucune donnée de conversation IA pour le moment. Les indicateurs apparaîtront lorsque les clients utiliseront le chat IA.',
    'dash.systemHealth': 'État du système',
    'dash.businessHealth': 'État de l’entreprise',
    'dash.healthy': 'Opérationnel',
    'dash.degraded': 'Dégradé',
    'dash.unavailable': 'Indisponible',
    'dash.someServicesDegraded': 'Certains services peuvent être dégradés',
    'dash.checkingServices': 'Vérification des services…',
    'dash.notConfigured': 'Non configuré',
    'dash.lastChecked': 'Dernière vérification :',
    'dash.loadingDashboard': 'Chargement du tableau de bord...',
    'dash.loadingBusinessInfo': 'Veuillez patienter pendant le chargement des informations de votre entreprise.',
    'dash.loadingDashboardWait': 'Veuillez patienter pendant le chargement de votre tableau de bord.',
    'dash.loadingBusinessData': 'Chargement des données de l’entreprise...',
    'dash.readOnly.title': 'Le compte est en mode lecture seule',
    'dash.readOnly.desc': 'Réactivez votre abonnement pour utiliser {label}.',
    'dash.readOnly.tooltip': 'Mode lecture seule — réactivez pour déverrouiller',
    'navdesc.dashboardOverview': 'Aperçu du tableau de bord',
    'navdesc.monitorAiVoice': 'Surveiller les interactions vocales IA',
    'navdesc.testAiVoiceComplex': 'Tester la voix IA avec des scénarios complexes',
    'navdesc.makeIntegratedCalls': 'Passer des appels intégrés',
    'navdesc.manageContacts': 'Gérer vos contacts',
    'navdesc.browseProducts': 'Parcourir les produits et services',
    'navdesc.participateSurveys': 'Participer aux sondages',
    'navdesc.collaborateTeam': 'Collaborer avec les membres de l’équipe',
    'navdesc.allInOneAgent': 'Interface d’agent tout-en-un',
    'navdesc.manageCustomerTickets': 'Gérer les tickets clients',
    'navdesc.handleEscalated': 'Gérer les conversations escaladées',
    'navdesc.viewSolutions': 'Voir les solutions',
    'navdesc.aiVoice30': 'Voix IA avec plus de 30 scénarios',
    'navdesc.aiChat30': 'Chat IA avec plus de 30 scénarios',
    'navdesc.customerManagement': 'Gestion des clients',
    'navdesc.monitorTeamPerf': 'Suivre les performances de l’équipe',
    'navdesc.manageTeamRoles': 'Gérer les membres et rôles de l’équipe',
    'navdesc.advancedTicketOps': 'Opérations avancées sur les tickets',
    'navdesc.approveReject': 'Approuver ou rejeter les solutions soumises',
    'navdesc.manageFeedbackCats': 'Gérer les catégories de retours',
    'navdesc.reportsInsights': 'Rapports, analyses et métriques IA',
    'navdesc.manageShowroom': 'Gérer la salle d’exposition de l’entreprise',
    'navdesc.createSurveys': 'Créer des sondages clients',
    'navdesc.unifiedDataAccess': 'Accès unifié aux données',
    'navdesc.aiPerfAnalytics': 'Analyse des performances IA',
    'navdesc.advancedReporting': 'Rapports avancés et métriques IA',
    'navdesc.teamResourceMgmt': 'Gestion des équipes et des ressources',
    'navdesc.strategicInsights': 'Analyses stratégiques',
    'navdesc.strategicAnalytics': 'Analyses stratégiques et métriques IA',
    'navdesc.cLevelInsights': 'Analyses pour la direction',
    'navdesc.revenueRoi': 'Analyse des revenus et du ROI',
    'navdesc.executiveReporting': 'Rapports exécutifs et métriques IA',
    'navdesc.apiSystemMgmt': 'Gestion des API et du système',
    'navdesc.perfSystemLogs': 'Performances et journaux système',
    'navdesc.systemAnalytics': 'Analyses système et métriques IA',
    'navdesc.systemConfig': 'Configuration du système',
    'navdesc.manageTeamMembers': 'Gérer les membres de l’équipe',
    'navdesc.planInvoices': 'Forfait, factures, mise à niveau',
    'navdesc.allTicketAccess': 'Accès à tous les tickets',
    'navdesc.knowledgeBase': 'Base de connaissances',
    'navdesc.customerOversight': 'Supervision des clients',
    'navdesc.fullAnalytics': 'Analyses complètes et métriques IA',
  },
  pt: {
    'dash.greeting.admin': 'Visão geral e gestão do sistema',
    'dash.greeting.operator': 'Painel de atendimento ao cliente',
    'dash.greeting.manager': 'Visão geral do desempenho da equipe',
    'dash.greeting.analyst': 'Painel de análise de dados',
    'dash.greeting.director': 'Painel de insights estratégicos',
    'dash.greeting.executive': 'Painel de resumo executivo',
    'dash.greeting.it': 'Painel de operações técnicas',
    'dash.greeting.super_admin': 'Torre de controle global',
    'dash.greeting.default': 'Visão geral do painel',
    'dash.welcome': 'Bem-vindo de volta, {name}!',
    'dash.brandId': 'ID da marca:',
    'dash.stat.totalTickets': 'Total de tickets',
    'dash.stat.resolved': 'Resolvidos',
    'dash.stat.activeEscalations': 'Escalações ativas',
    'dash.stat.conversations': 'Conversas',
    'dash.stat.teamMembers': 'Membros da equipe',
    'dash.callQueue.title': 'Fila de chamadas recebidas',
    'dash.callQueue.subtitle': 'Fila de escalações ao vivo para {business}',
    'dash.callQueue.checking': 'Verificando a fila...',
    'dash.callQueue.live': 'Ao vivo',
    'dash.callQueue.callsWaiting': '{count} na fila',
    'dash.callQueue.customer': 'Cliente:',
    'dash.callQueue.reason': 'Motivo:',
    'dash.callQueue.status': 'Status:',
    'dash.callQueue.dismiss': 'Dispensar',
    'dash.callQueue.acceptCall': 'Aceitar chamada',
    'dash.callQueue.empty': 'Nenhuma chamada recebida na fila',
    'dash.callQueue.emptyDetail': 'A fila está vazia no momento.',
    'dash.callQueue.openDesktop': 'Abrir área de trabalho do agente',
    'dash.newTicket.title': 'Criar novo ticket',
    'dash.newTicket.subtitle': 'Crie um ticket de suporte para um cliente',
    'dash.newTicket.customer': 'Cliente *',
    'dash.newTicket.customerPlaceholder': 'Nome ou e-mail do cliente',
    'dash.newTicket.issueTitle': 'Título do problema *',
    'dash.newTicket.issueTitlePlaceholder': 'Breve descrição do problema',
    'dash.newTicket.category': 'Categoria',
    'dash.newTicket.selectCategory': 'Selecionar categoria',
    'dash.newTicket.catTechnical': 'Suporte técnico',
    'dash.newTicket.catBilling': 'Faturamento',
    'dash.newTicket.catGeneral': 'Consulta geral',
    'dash.newTicket.catAccount': 'Gestão de conta',
    'dash.newTicket.catProduct': 'Suporte de produto',
    'dash.newTicket.priority': 'Prioridade',
    'dash.newTicket.priorityLow': 'Baixa',
    'dash.newTicket.priorityMedium': 'Média',
    'dash.newTicket.priorityHigh': 'Alta',
    'dash.newTicket.priorityUrgent': 'Urgente',
    'dash.newTicket.description': 'Descrição *',
    'dash.newTicket.descriptionPlaceholder': 'Descrição detalhada do problema',
    'dash.newTicket.create': 'Criar ticket',
    'dash.toast.missingInfo': 'Informações ausentes',
    'dash.toast.missingInfoDetail': 'Preencha todos os campos obrigatórios',
    'dash.toast.ticketCreated': 'Ticket criado',
    'dash.toast.ticketCreatedDetail': 'Ticket de suporte criado com sucesso',
    'dash.toast.ticketError': 'Falha ao criar o ticket. Tente novamente.',
    'dash.sampleData': 'Dados de amostra',
    'dash.notConnectedShort': 'Ainda não conectado a uma fonte ao vivo.',
    'dash.notConnectedLong': 'Esta métrica ainda não está conectada a uma fonte de dados ao vivo.',
    'dash.live': 'AO VIVO',
    'dash.recentActivity': 'Atividade recente',
    'dash.loadingActivity': 'Carregando atividade...',
    'dash.noActivity': 'Ainda não há atividade recente.',
    'dash.performanceMetrics': 'Métricas de desempenho',
    'dash.loadingMetrics': 'Carregando métricas...',
    'dash.autoResolutionRate': 'Taxa de resolução automática',
    'dash.escalationRate': 'Taxa de escalação',
    'dash.totalConversationsAnalyzed': 'Total de conversas de IA analisadas',
    'dash.noConversationData': 'Ainda não há dados de conversas de IA. As métricas aparecerão quando os clientes usarem o chat de IA.',
    'dash.systemHealth': 'Integridade do sistema',
    'dash.businessHealth': 'Integridade do negócio',
    'dash.healthy': 'Saudável',
    'dash.degraded': 'Degradado',
    'dash.unavailable': 'Indisponível',
    'dash.someServicesDegraded': 'Alguns serviços podem estar degradados',
    'dash.checkingServices': 'Verificando serviços…',
    'dash.notConfigured': 'Não configurado',
    'dash.lastChecked': 'Última verificação:',
    'dash.loadingDashboard': 'Carregando o painel...',
    'dash.loadingBusinessInfo': 'Aguarde enquanto carregamos as informações da sua empresa.',
    'dash.loadingDashboardWait': 'Aguarde enquanto carregamos o seu painel.',
    'dash.loadingBusinessData': 'Carregando dados da empresa...',
    'dash.readOnly.title': 'A conta está no modo somente leitura',
    'dash.readOnly.desc': 'Reative sua assinatura para usar {label}.',
    'dash.readOnly.tooltip': 'Modo somente leitura — reative para desbloquear',
    'navdesc.dashboardOverview': 'Visão geral do painel',
    'navdesc.monitorAiVoice': 'Monitorar interações de voz IA',
    'navdesc.testAiVoiceComplex': 'Testar a voz IA com cenários complexos',
    'navdesc.makeIntegratedCalls': 'Fazer chamadas integradas',
    'navdesc.manageContacts': 'Gerenciar seus contatos',
    'navdesc.browseProducts': 'Explorar produtos e serviços',
    'navdesc.participateSurveys': 'Participar de pesquisas',
    'navdesc.collaborateTeam': 'Colaborar com os membros da equipe',
    'navdesc.allInOneAgent': 'Interface de agente tudo-em-um',
    'navdesc.manageCustomerTickets': 'Gerenciar tickets de clientes',
    'navdesc.handleEscalated': 'Lidar com conversas escaladas',
    'navdesc.viewSolutions': 'Ver soluções',
    'navdesc.aiVoice30': 'Voz IA com mais de 30 cenários',
    'navdesc.aiChat30': 'Chat IA com mais de 30 cenários',
    'navdesc.customerManagement': 'Gestão de clientes',
    'navdesc.monitorTeamPerf': 'Monitorar o desempenho da equipe',
    'navdesc.manageTeamRoles': 'Gerenciar membros e funções da equipe',
    'navdesc.advancedTicketOps': 'Operações avançadas de tickets',
    'navdesc.approveReject': 'Aprovar ou rejeitar soluções enviadas',
    'navdesc.manageFeedbackCats': 'Gerenciar categorias de feedback',
    'navdesc.reportsInsights': 'Relatórios, insights e métricas de IA',
    'navdesc.manageShowroom': 'Gerenciar o showroom do negócio',
    'navdesc.createSurveys': 'Criar pesquisas para clientes',
    'navdesc.unifiedDataAccess': 'Acesso unificado aos dados',
    'navdesc.aiPerfAnalytics': 'Análise de desempenho de IA',
    'navdesc.advancedReporting': 'Relatórios avançados e métricas de IA',
    'navdesc.teamResourceMgmt': 'Gestão de equipe e recursos',
    'navdesc.strategicInsights': 'Insights estratégicos',
    'navdesc.strategicAnalytics': 'Análises estratégicas e métricas de IA',
    'navdesc.cLevelInsights': 'Insights para a alta direção',
    'navdesc.revenueRoi': 'Análise de receita e ROI',
    'navdesc.executiveReporting': 'Relatórios executivos e métricas de IA',
    'navdesc.apiSystemMgmt': 'Gestão de API e sistema',
    'navdesc.perfSystemLogs': 'Desempenho e logs do sistema',
    'navdesc.systemAnalytics': 'Análises do sistema e métricas de IA',
    'navdesc.systemConfig': 'Configuração do sistema',
    'navdesc.manageTeamMembers': 'Gerenciar membros da equipe',
    'navdesc.planInvoices': 'Plano, faturas, upgrade',
    'navdesc.allTicketAccess': 'Acesso a todos os tickets',
    'navdesc.knowledgeBase': 'Base de conhecimento',
    'navdesc.customerOversight': 'Supervisão de clientes',
    'navdesc.fullAnalytics': 'Análises completas e métricas de IA',
  },
  de: {
    'dash.greeting.admin': 'Systemübersicht & Verwaltung',
    'dash.greeting.operator': 'Kundenservice-Dashboard',
    'dash.greeting.manager': 'Übersicht der Teamleistung',
    'dash.greeting.analyst': 'Datenanalyse-Dashboard',
    'dash.greeting.director': 'Dashboard für strategische Einblicke',
    'dash.greeting.executive': 'Dashboard der Führungsübersicht',
    'dash.greeting.it': 'Dashboard für technischen Betrieb',
    'dash.greeting.super_admin': 'Globaler Kontrollturm',
    'dash.greeting.default': 'Dashboard-Übersicht',
    'dash.welcome': 'Willkommen zurück, {name}!',
    'dash.brandId': 'Marken-ID:',
    'dash.stat.totalTickets': 'Tickets gesamt',
    'dash.stat.resolved': 'Gelöst',
    'dash.stat.activeEscalations': 'Aktive Eskalationen',
    'dash.stat.conversations': 'Konversationen',
    'dash.stat.teamMembers': 'Teammitglieder',
    'dash.callQueue.title': 'Warteschlange eingehender Anrufe',
    'dash.callQueue.subtitle': 'Live-Eskalations-Warteschlange für {business}',
    'dash.callQueue.checking': 'Warteschlange wird geprüft...',
    'dash.callQueue.live': 'Live',
    'dash.callQueue.callsWaiting': '{count} in der Warteschlange',
    'dash.callQueue.customer': 'Kunde:',
    'dash.callQueue.reason': 'Grund:',
    'dash.callQueue.status': 'Status:',
    'dash.callQueue.dismiss': 'Verwerfen',
    'dash.callQueue.acceptCall': 'Anruf annehmen',
    'dash.callQueue.empty': 'Keine eingehenden Anrufe in der Warteschlange',
    'dash.callQueue.emptyDetail': 'Die Warteschlange ist derzeit leer.',
    'dash.callQueue.openDesktop': 'Agenten-Desktop öffnen',
    'dash.newTicket.title': 'Neues Ticket erstellen',
    'dash.newTicket.subtitle': 'Erstellen Sie ein Support-Ticket für einen Kunden',
    'dash.newTicket.customer': 'Kunde *',
    'dash.newTicket.customerPlaceholder': 'Name oder E-Mail des Kunden',
    'dash.newTicket.issueTitle': 'Titel des Problems *',
    'dash.newTicket.issueTitlePlaceholder': 'Kurze Beschreibung des Problems',
    'dash.newTicket.category': 'Kategorie',
    'dash.newTicket.selectCategory': 'Kategorie auswählen',
    'dash.newTicket.catTechnical': 'Technischer Support',
    'dash.newTicket.catBilling': 'Abrechnung',
    'dash.newTicket.catGeneral': 'Allgemeine Anfrage',
    'dash.newTicket.catAccount': 'Kontoverwaltung',
    'dash.newTicket.catProduct': 'Produktsupport',
    'dash.newTicket.priority': 'Priorität',
    'dash.newTicket.priorityLow': 'Niedrig',
    'dash.newTicket.priorityMedium': 'Mittel',
    'dash.newTicket.priorityHigh': 'Hoch',
    'dash.newTicket.priorityUrgent': 'Dringend',
    'dash.newTicket.description': 'Beschreibung *',
    'dash.newTicket.descriptionPlaceholder': 'Detaillierte Beschreibung des Problems',
    'dash.newTicket.create': 'Ticket erstellen',
    'dash.toast.missingInfo': 'Fehlende Informationen',
    'dash.toast.missingInfoDetail': 'Bitte füllen Sie alle Pflichtfelder aus',
    'dash.toast.ticketCreated': 'Ticket erstellt',
    'dash.toast.ticketCreatedDetail': 'Support-Ticket erfolgreich erstellt',
    'dash.toast.ticketError': 'Ticket konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
    'dash.sampleData': 'Beispieldaten',
    'dash.notConnectedShort': 'Noch nicht mit einer Live-Quelle verbunden.',
    'dash.notConnectedLong': 'Diese Kennzahl ist noch nicht mit einer Live-Datenquelle verbunden.',
    'dash.live': 'LIVE',
    'dash.recentActivity': 'Letzte Aktivität',
    'dash.loadingActivity': 'Aktivität wird geladen...',
    'dash.noActivity': 'Noch keine aktuelle Aktivität.',
    'dash.performanceMetrics': 'Leistungskennzahlen',
    'dash.loadingMetrics': 'Kennzahlen werden geladen...',
    'dash.autoResolutionRate': 'Automatische Lösungsrate',
    'dash.escalationRate': 'Eskalationsrate',
    'dash.totalConversationsAnalyzed': 'Analysierte KI-Konversationen insgesamt',
    'dash.noConversationData': 'Noch keine KI-Konversationsdaten. Die Kennzahlen erscheinen, sobald Kunden den KI-Chat nutzen.',
    'dash.systemHealth': 'Systemzustand',
    'dash.businessHealth': 'Unternehmenszustand',
    'dash.healthy': 'Intakt',
    'dash.degraded': 'Beeinträchtigt',
    'dash.unavailable': 'Nicht verfügbar',
    'dash.someServicesDegraded': 'Einige Dienste sind möglicherweise beeinträchtigt',
    'dash.checkingServices': 'Dienste werden geprüft…',
    'dash.notConfigured': 'Nicht konfiguriert',
    'dash.lastChecked': 'Zuletzt geprüft:',
    'dash.loadingDashboard': 'Dashboard wird geladen...',
    'dash.loadingBusinessInfo': 'Bitte warten Sie, während wir Ihre Unternehmensinformationen laden.',
    'dash.loadingDashboardWait': 'Bitte warten Sie, während wir Ihr Dashboard laden.',
    'dash.loadingBusinessData': 'Unternehmensdaten werden geladen...',
    'dash.readOnly.title': 'Konto befindet sich im schreibgeschützten Modus',
    'dash.readOnly.desc': 'Reaktivieren Sie Ihr Abonnement, um {label} zu nutzen.',
    'dash.readOnly.tooltip': 'Schreibgeschützter Modus – zum Entsperren reaktivieren',
    'navdesc.dashboardOverview': 'Dashboard-Übersicht',
    'navdesc.monitorAiVoice': 'KI-Sprachinteraktionen überwachen',
    'navdesc.testAiVoiceComplex': 'KI-Sprache mit komplexen Szenarien testen',
    'navdesc.makeIntegratedCalls': 'Integrierte Anrufe tätigen',
    'navdesc.manageContacts': 'Ihre Kontakte verwalten',
    'navdesc.browseProducts': 'Produkte und Dienstleistungen durchsuchen',
    'navdesc.participateSurveys': 'An Umfragen teilnehmen',
    'navdesc.collaborateTeam': 'Mit Teammitgliedern zusammenarbeiten',
    'navdesc.allInOneAgent': 'All-in-One-Agenten-Oberfläche',
    'navdesc.manageCustomerTickets': 'Kundentickets verwalten',
    'navdesc.handleEscalated': 'Eskalierte Konversationen bearbeiten',
    'navdesc.viewSolutions': 'Lösungen anzeigen',
    'navdesc.aiVoice30': 'KI-Sprache mit über 30 Szenarien',
    'navdesc.aiChat30': 'KI-Chat mit über 30 Szenarien',
    'navdesc.customerManagement': 'Kundenverwaltung',
    'navdesc.monitorTeamPerf': 'Teamleistung überwachen',
    'navdesc.manageTeamRoles': 'Teammitglieder & Rollen verwalten',
    'navdesc.advancedTicketOps': 'Erweiterte Ticket-Vorgänge',
    'navdesc.approveReject': 'Eingereichte Lösungen genehmigen oder ablehnen',
    'navdesc.manageFeedbackCats': 'Feedback-Kategorien verwalten',
    'navdesc.reportsInsights': 'Berichte, Einblicke & KI-Kennzahlen',
    'navdesc.manageShowroom': 'Unternehmens-Showroom verwalten',
    'navdesc.createSurveys': 'Kundenumfragen erstellen',
    'navdesc.unifiedDataAccess': 'Einheitlicher Datenzugriff',
    'navdesc.aiPerfAnalytics': 'KI-Leistungsanalysen',
    'navdesc.advancedReporting': 'Erweiterte Berichte & KI-Kennzahlen',
    'navdesc.teamResourceMgmt': 'Team- & Ressourcenverwaltung',
    'navdesc.strategicInsights': 'Strategische Einblicke',
    'navdesc.strategicAnalytics': 'Strategische Analysen & KI-Kennzahlen',
    'navdesc.cLevelInsights': 'Einblicke für die Führungsebene',
    'navdesc.revenueRoi': 'Umsatz- & ROI-Analyse',
    'navdesc.executiveReporting': 'Führungsberichte & KI-Kennzahlen',
    'navdesc.apiSystemMgmt': 'API- & Systemverwaltung',
    'navdesc.perfSystemLogs': 'Leistung & Systemprotokolle',
    'navdesc.systemAnalytics': 'Systemanalysen & KI-Kennzahlen',
    'navdesc.systemConfig': 'Systemkonfiguration',
    'navdesc.manageTeamMembers': 'Teammitglieder verwalten',
    'navdesc.planInvoices': 'Tarif, Rechnungen, Upgrade',
    'navdesc.allTicketAccess': 'Zugriff auf alle Tickets',
    'navdesc.knowledgeBase': 'Wissensdatenbank',
    'navdesc.customerOversight': 'Kundenaufsicht',
    'navdesc.fullAnalytics': 'Vollständige Analysen & KI-Kennzahlen',
  },
  it: {
    'dash.greeting.admin': 'Panoramica e gestione del sistema',
    'dash.greeting.operator': 'Dashboard del servizio clienti',
    'dash.greeting.manager': 'Panoramica delle prestazioni del team',
    'dash.greeting.analyst': 'Dashboard di analisi dei dati',
    'dash.greeting.director': 'Dashboard degli approfondimenti strategici',
    'dash.greeting.executive': 'Dashboard del riepilogo esecutivo',
    'dash.greeting.it': 'Dashboard delle operazioni tecniche',
    'dash.greeting.super_admin': 'Torre di controllo globale',
    'dash.greeting.default': 'Panoramica della dashboard',
    'dash.welcome': 'Bentornato, {name}!',
    'dash.brandId': 'ID marchio:',
    'dash.stat.totalTickets': 'Ticket totali',
    'dash.stat.resolved': 'Risolti',
    'dash.stat.activeEscalations': 'Escalation attive',
    'dash.stat.conversations': 'Conversazioni',
    'dash.stat.teamMembers': 'Membri del team',
    'dash.callQueue.title': 'Coda delle chiamate in arrivo',
    'dash.callQueue.subtitle': 'Coda di escalation in tempo reale per {business}',
    'dash.callQueue.checking': 'Verifica della coda...',
    'dash.callQueue.live': 'In diretta',
    'dash.callQueue.callsWaiting': '{count} in coda',
    'dash.callQueue.customer': 'Cliente:',
    'dash.callQueue.reason': 'Motivo:',
    'dash.callQueue.status': 'Stato:',
    'dash.callQueue.dismiss': 'Ignora',
    'dash.callQueue.acceptCall': 'Accetta chiamata',
    'dash.callQueue.empty': 'Nessuna chiamata in arrivo in coda',
    'dash.callQueue.emptyDetail': 'La coda è attualmente vuota.',
    'dash.callQueue.openDesktop': 'Apri desktop dell’agente',
    'dash.newTicket.title': 'Crea nuovo ticket',
    'dash.newTicket.subtitle': 'Crea un ticket di assistenza per un cliente',
    'dash.newTicket.customer': 'Cliente *',
    'dash.newTicket.customerPlaceholder': 'Nome o email del cliente',
    'dash.newTicket.issueTitle': 'Titolo del problema *',
    'dash.newTicket.issueTitlePlaceholder': 'Breve descrizione del problema',
    'dash.newTicket.category': 'Categoria',
    'dash.newTicket.selectCategory': 'Seleziona categoria',
    'dash.newTicket.catTechnical': 'Supporto tecnico',
    'dash.newTicket.catBilling': 'Fatturazione',
    'dash.newTicket.catGeneral': 'Richiesta generale',
    'dash.newTicket.catAccount': 'Gestione account',
    'dash.newTicket.catProduct': 'Supporto prodotto',
    'dash.newTicket.priority': 'Priorità',
    'dash.newTicket.priorityLow': 'Bassa',
    'dash.newTicket.priorityMedium': 'Media',
    'dash.newTicket.priorityHigh': 'Alta',
    'dash.newTicket.priorityUrgent': 'Urgente',
    'dash.newTicket.description': 'Descrizione *',
    'dash.newTicket.descriptionPlaceholder': 'Descrizione dettagliata del problema',
    'dash.newTicket.create': 'Crea ticket',
    'dash.toast.missingInfo': 'Informazioni mancanti',
    'dash.toast.missingInfoDetail': 'Compila tutti i campi obbligatori',
    'dash.toast.ticketCreated': 'Ticket creato',
    'dash.toast.ticketCreatedDetail': 'Ticket di assistenza creato con successo',
    'dash.toast.ticketError': 'Impossibile creare il ticket. Riprova.',
    'dash.sampleData': 'Dati di esempio',
    'dash.notConnectedShort': 'Non ancora collegato a una fonte in tempo reale.',
    'dash.notConnectedLong': 'Questa metrica non è ancora collegata a una fonte di dati in tempo reale.',
    'dash.live': 'IN DIRETTA',
    'dash.recentActivity': 'Attività recente',
    'dash.loadingActivity': 'Caricamento attività...',
    'dash.noActivity': 'Nessuna attività recente per ora.',
    'dash.performanceMetrics': 'Metriche di prestazione',
    'dash.loadingMetrics': 'Caricamento metriche...',
    'dash.autoResolutionRate': 'Tasso di risoluzione automatica',
    'dash.escalationRate': 'Tasso di escalation',
    'dash.totalConversationsAnalyzed': 'Totale conversazioni IA analizzate',
    'dash.noConversationData': 'Nessun dato sulle conversazioni IA per ora. Le metriche appariranno quando i clienti useranno la chat IA.',
    'dash.systemHealth': 'Stato del sistema',
    'dash.businessHealth': 'Stato dell’attività',
    'dash.healthy': 'Integro',
    'dash.degraded': 'Degradato',
    'dash.unavailable': 'Non disponibile',
    'dash.someServicesDegraded': 'Alcuni servizi potrebbero essere degradati',
    'dash.checkingServices': 'Verifica dei servizi…',
    'dash.notConfigured': 'Non configurato',
    'dash.lastChecked': 'Ultimo controllo:',
    'dash.loadingDashboard': 'Caricamento della dashboard...',
    'dash.loadingBusinessInfo': 'Attendi mentre carichiamo le informazioni della tua azienda.',
    'dash.loadingDashboardWait': 'Attendi mentre carichiamo la tua dashboard.',
    'dash.loadingBusinessData': 'Caricamento dei dati aziendali...',
    'dash.readOnly.title': 'L’account è in modalità di sola lettura',
    'dash.readOnly.desc': 'Riattiva il tuo abbonamento per usare {label}.',
    'dash.readOnly.tooltip': 'Modalità di sola lettura — riattiva per sbloccare',
    'navdesc.dashboardOverview': 'Panoramica della dashboard',
    'navdesc.monitorAiVoice': 'Monitora le interazioni vocali IA',
    'navdesc.testAiVoiceComplex': 'Testa la voce IA con scenari complessi',
    'navdesc.makeIntegratedCalls': 'Effettua chiamate integrate',
    'navdesc.manageContacts': 'Gestisci i tuoi contatti',
    'navdesc.browseProducts': 'Sfoglia prodotti e servizi',
    'navdesc.participateSurveys': 'Partecipa ai sondaggi',
    'navdesc.collaborateTeam': 'Collabora con i membri del team',
    'navdesc.allInOneAgent': 'Interfaccia agente all-in-one',
    'navdesc.manageCustomerTickets': 'Gestisci i ticket dei clienti',
    'navdesc.handleEscalated': 'Gestisci le conversazioni inoltrate',
    'navdesc.viewSolutions': 'Visualizza soluzioni',
    'navdesc.aiVoice30': 'Voce IA con oltre 30 scenari',
    'navdesc.aiChat30': 'Chat IA con oltre 30 scenari',
    'navdesc.customerManagement': 'Gestione clienti',
    'navdesc.monitorTeamPerf': 'Monitora le prestazioni del team',
    'navdesc.manageTeamRoles': 'Gestisci membri e ruoli del team',
    'navdesc.advancedTicketOps': 'Operazioni avanzate sui ticket',
    'navdesc.approveReject': 'Approva o rifiuta le soluzioni inviate',
    'navdesc.manageFeedbackCats': 'Gestisci le categorie di feedback',
    'navdesc.reportsInsights': 'Report, approfondimenti e metriche IA',
    'navdesc.manageShowroom': 'Gestisci lo showroom aziendale',
    'navdesc.createSurveys': 'Crea sondaggi per i clienti',
    'navdesc.unifiedDataAccess': 'Accesso unificato ai dati',
    'navdesc.aiPerfAnalytics': 'Analisi delle prestazioni IA',
    'navdesc.advancedReporting': 'Report avanzati e metriche IA',
    'navdesc.teamResourceMgmt': 'Gestione team e risorse',
    'navdesc.strategicInsights': 'Approfondimenti strategici',
    'navdesc.strategicAnalytics': 'Analisi strategiche e metriche IA',
    'navdesc.cLevelInsights': 'Approfondimenti per i dirigenti',
    'navdesc.revenueRoi': 'Analisi dei ricavi e del ROI',
    'navdesc.executiveReporting': 'Report esecutivi e metriche IA',
    'navdesc.apiSystemMgmt': 'Gestione API e sistema',
    'navdesc.perfSystemLogs': 'Prestazioni e registri di sistema',
    'navdesc.systemAnalytics': 'Analisi di sistema e metriche IA',
    'navdesc.systemConfig': 'Configurazione del sistema',
    'navdesc.manageTeamMembers': 'Gestisci i membri del team',
    'navdesc.planInvoices': 'Piano, fatture, upgrade',
    'navdesc.allTicketAccess': 'Accesso a tutti i ticket',
    'navdesc.knowledgeBase': 'Base di conoscenza',
    'navdesc.customerOversight': 'Supervisione dei clienti',
    'navdesc.fullAnalytics': 'Analisi complete e metriche IA',
  },
  ja: {
    'dash.greeting.admin': 'システム概要と管理',
    'dash.greeting.operator': 'カスタマーサービスダッシュボード',
    'dash.greeting.manager': 'チームパフォーマンス概要',
    'dash.greeting.analyst': 'データ分析ダッシュボード',
    'dash.greeting.director': '戦略的インサイトダッシュボード',
    'dash.greeting.executive': 'エグゼクティブサマリーダッシュボード',
    'dash.greeting.it': '技術運用ダッシュボード',
    'dash.greeting.super_admin': 'グローバルコントロールタワー',
    'dash.greeting.default': 'ダッシュボード概要',
    'dash.welcome': 'おかえりなさい、{name}さん！',
    'dash.brandId': 'ブランドID：',
    'dash.stat.totalTickets': '総チケット数',
    'dash.stat.resolved': '解決済み',
    'dash.stat.activeEscalations': 'アクティブなエスカレーション',
    'dash.stat.conversations': '会話',
    'dash.stat.teamMembers': 'チームメンバー',
    'dash.callQueue.title': '着信キュー',
    'dash.callQueue.subtitle': '{business} のライブエスカレーションキュー',
    'dash.callQueue.checking': 'キューを確認中...',
    'dash.callQueue.live': 'ライブ',
    'dash.callQueue.callsWaiting': 'キューに {count} 件',
    'dash.callQueue.customer': '顧客：',
    'dash.callQueue.reason': '理由：',
    'dash.callQueue.status': 'ステータス：',
    'dash.callQueue.dismiss': '却下',
    'dash.callQueue.acceptCall': '通話に応答',
    'dash.callQueue.empty': 'キューに着信はありません',
    'dash.callQueue.emptyDetail': '現在キューは空です。',
    'dash.callQueue.openDesktop': 'エージェントデスクトップを開く',
    'dash.newTicket.title': '新規チケットを作成',
    'dash.newTicket.subtitle': '顧客のサポートチケットを作成します',
    'dash.newTicket.customer': '顧客 *',
    'dash.newTicket.customerPlaceholder': '顧客名またはメールアドレス',
    'dash.newTicket.issueTitle': '問題のタイトル *',
    'dash.newTicket.issueTitlePlaceholder': '問題の簡単な説明',
    'dash.newTicket.category': 'カテゴリー',
    'dash.newTicket.selectCategory': 'カテゴリーを選択',
    'dash.newTicket.catTechnical': 'テクニカルサポート',
    'dash.newTicket.catBilling': '請求',
    'dash.newTicket.catGeneral': '一般的な問い合わせ',
    'dash.newTicket.catAccount': 'アカウント管理',
    'dash.newTicket.catProduct': '製品サポート',
    'dash.newTicket.priority': '優先度',
    'dash.newTicket.priorityLow': '低',
    'dash.newTicket.priorityMedium': '中',
    'dash.newTicket.priorityHigh': '高',
    'dash.newTicket.priorityUrgent': '緊急',
    'dash.newTicket.description': '説明 *',
    'dash.newTicket.descriptionPlaceholder': '問題の詳細な説明',
    'dash.newTicket.create': 'チケットを作成',
    'dash.toast.missingInfo': '情報が不足しています',
    'dash.toast.missingInfoDetail': '必須項目をすべて入力してください',
    'dash.toast.ticketCreated': 'チケットが作成されました',
    'dash.toast.ticketCreatedDetail': 'サポートチケットが正常に作成されました',
    'dash.toast.ticketError': 'チケットを作成できませんでした。もう一度お試しください。',
    'dash.sampleData': 'サンプルデータ',
    'dash.notConnectedShort': 'まだライブソースに接続されていません。',
    'dash.notConnectedLong': 'この指標はまだライブデータソースに接続されていません。',
    'dash.live': 'ライブ',
    'dash.recentActivity': '最近のアクティビティ',
    'dash.loadingActivity': 'アクティビティを読み込み中...',
    'dash.noActivity': 'まだ最近のアクティビティはありません。',
    'dash.performanceMetrics': 'パフォーマンス指標',
    'dash.loadingMetrics': '指標を読み込み中...',
    'dash.autoResolutionRate': '自動解決率',
    'dash.escalationRate': 'エスカレーション率',
    'dash.totalConversationsAnalyzed': '分析されたAI会話の合計',
    'dash.noConversationData': 'AI会話データはまだありません。顧客がAIチャットを使用すると指標が表示されます。',
    'dash.systemHealth': 'システム正常性',
    'dash.businessHealth': 'ビジネスの正常性',
    'dash.healthy': '正常',
    'dash.degraded': '低下',
    'dash.unavailable': '利用不可',
    'dash.someServicesDegraded': '一部のサービスが低下している可能性があります',
    'dash.checkingServices': 'サービスを確認中…',
    'dash.notConfigured': '未設定',
    'dash.lastChecked': '最終確認：',
    'dash.loadingDashboard': 'ダッシュボードを読み込み中...',
    'dash.loadingBusinessInfo': '会社情報を読み込んでいます。お待ちください。',
    'dash.loadingDashboardWait': 'ダッシュボードを読み込んでいます。お待ちください。',
    'dash.loadingBusinessData': 'ビジネスデータを読み込み中...',
    'dash.readOnly.title': 'アカウントは読み取り専用モードです',
    'dash.readOnly.desc': '{label} を使用するにはサブスクリプションを再有効化してください。',
    'dash.readOnly.tooltip': '読み取り専用モード — 再有効化してロック解除',
    'navdesc.dashboardOverview': 'ダッシュボード概要',
    'navdesc.monitorAiVoice': 'AI音声の対話を監視',
    'navdesc.testAiVoiceComplex': '複雑なシナリオでAI音声をテスト',
    'navdesc.makeIntegratedCalls': '統合通話を発信',
    'navdesc.manageContacts': '連絡先を管理',
    'navdesc.browseProducts': '商品とサービスを閲覧',
    'navdesc.participateSurveys': 'アンケートに参加',
    'navdesc.collaborateTeam': 'チームメンバーと連携',
    'navdesc.allInOneAgent': 'オールインワンのエージェントインターフェース',
    'navdesc.manageCustomerTickets': '顧客チケットを管理',
    'navdesc.handleEscalated': 'エスカレーションされた会話を処理',
    'navdesc.viewSolutions': 'ソリューションを表示',
    'navdesc.aiVoice30': '30以上のシナリオのAI音声',
    'navdesc.aiChat30': '30以上のシナリオのAIチャット',
    'navdesc.customerManagement': '顧客管理',
    'navdesc.monitorTeamPerf': 'チームのパフォーマンスを監視',
    'navdesc.manageTeamRoles': 'チームメンバーと役割を管理',
    'navdesc.advancedTicketOps': '高度なチケット操作',
    'navdesc.approveReject': '提出されたソリューションを承認または拒否',
    'navdesc.manageFeedbackCats': 'フィードバックカテゴリーを管理',
    'navdesc.reportsInsights': 'レポート、インサイト、AI指標',
    'navdesc.manageShowroom': 'ビジネスショールームを管理',
    'navdesc.createSurveys': '顧客アンケートを作成',
    'navdesc.unifiedDataAccess': '統合データアクセス',
    'navdesc.aiPerfAnalytics': 'AIパフォーマンス分析',
    'navdesc.advancedReporting': '高度なレポートとAI指標',
    'navdesc.teamResourceMgmt': 'チームとリソースの管理',
    'navdesc.strategicInsights': '戦略的インサイト',
    'navdesc.strategicAnalytics': '戦略分析とAI指標',
    'navdesc.cLevelInsights': '経営層向けインサイト',
    'navdesc.revenueRoi': '収益とROIの分析',
    'navdesc.executiveReporting': 'エグゼクティブレポートとAI指標',
    'navdesc.apiSystemMgmt': 'APIとシステムの管理',
    'navdesc.perfSystemLogs': 'パフォーマンスとシステムログ',
    'navdesc.systemAnalytics': 'システム分析とAI指標',
    'navdesc.systemConfig': 'システム構成',
    'navdesc.manageTeamMembers': 'チームメンバーを管理',
    'navdesc.planInvoices': 'プラン、請求書、アップグレード',
    'navdesc.allTicketAccess': 'すべてのチケットへのアクセス',
    'navdesc.knowledgeBase': 'ナレッジベース',
    'navdesc.customerOversight': '顧客の監督',
    'navdesc.fullAnalytics': '完全な分析とAI指標',
  },
  ko: {
    'dash.greeting.admin': '시스템 개요 및 관리',
    'dash.greeting.operator': '고객 서비스 대시보드',
    'dash.greeting.manager': '팀 성과 개요',
    'dash.greeting.analyst': '데이터 분석 대시보드',
    'dash.greeting.director': '전략적 인사이트 대시보드',
    'dash.greeting.executive': '경영진 요약 대시보드',
    'dash.greeting.it': '기술 운영 대시보드',
    'dash.greeting.super_admin': '글로벌 관제탑',
    'dash.greeting.default': '대시보드 개요',
    'dash.welcome': '다시 오신 것을 환영합니다, {name}님!',
    'dash.brandId': '브랜드 ID:',
    'dash.stat.totalTickets': '전체 티켓',
    'dash.stat.resolved': '해결됨',
    'dash.stat.activeEscalations': '활성 에스컬레이션',
    'dash.stat.conversations': '대화',
    'dash.stat.teamMembers': '팀원',
    'dash.callQueue.title': '수신 통화 대기열',
    'dash.callQueue.subtitle': '{business}의 실시간 에스컬레이션 대기열',
    'dash.callQueue.checking': '대기열 확인 중...',
    'dash.callQueue.live': '실시간',
    'dash.callQueue.callsWaiting': '대기열에 {count}건',
    'dash.callQueue.customer': '고객:',
    'dash.callQueue.reason': '사유:',
    'dash.callQueue.status': '상태:',
    'dash.callQueue.dismiss': '무시',
    'dash.callQueue.acceptCall': '통화 수락',
    'dash.callQueue.empty': '대기열에 수신 통화가 없습니다',
    'dash.callQueue.emptyDetail': '현재 대기열이 비어 있습니다.',
    'dash.callQueue.openDesktop': '상담원 데스크톱 열기',
    'dash.newTicket.title': '새 티켓 만들기',
    'dash.newTicket.subtitle': '고객을 위한 지원 티켓을 만듭니다',
    'dash.newTicket.customer': '고객 *',
    'dash.newTicket.customerPlaceholder': '고객 이름 또는 이메일',
    'dash.newTicket.issueTitle': '문제 제목 *',
    'dash.newTicket.issueTitlePlaceholder': '문제에 대한 간단한 설명',
    'dash.newTicket.category': '카테고리',
    'dash.newTicket.selectCategory': '카테고리 선택',
    'dash.newTicket.catTechnical': '기술 지원',
    'dash.newTicket.catBilling': '결제',
    'dash.newTicket.catGeneral': '일반 문의',
    'dash.newTicket.catAccount': '계정 관리',
    'dash.newTicket.catProduct': '제품 지원',
    'dash.newTicket.priority': '우선순위',
    'dash.newTicket.priorityLow': '낮음',
    'dash.newTicket.priorityMedium': '보통',
    'dash.newTicket.priorityHigh': '높음',
    'dash.newTicket.priorityUrgent': '긴급',
    'dash.newTicket.description': '설명 *',
    'dash.newTicket.descriptionPlaceholder': '문제에 대한 자세한 설명',
    'dash.newTicket.create': '티켓 만들기',
    'dash.toast.missingInfo': '정보 누락',
    'dash.toast.missingInfoDetail': '모든 필수 항목을 입력하세요',
    'dash.toast.ticketCreated': '티켓이 생성되었습니다',
    'dash.toast.ticketCreatedDetail': '지원 티켓이 성공적으로 생성되었습니다',
    'dash.toast.ticketError': '티켓을 생성하지 못했습니다. 다시 시도하세요.',
    'dash.sampleData': '샘플 데이터',
    'dash.notConnectedShort': '아직 실시간 소스에 연결되지 않았습니다.',
    'dash.notConnectedLong': '이 지표는 아직 실시간 데이터 소스에 연결되지 않았습니다.',
    'dash.live': '실시간',
    'dash.recentActivity': '최근 활동',
    'dash.loadingActivity': '활동 불러오는 중...',
    'dash.noActivity': '아직 최근 활동이 없습니다.',
    'dash.performanceMetrics': '성과 지표',
    'dash.loadingMetrics': '지표 불러오는 중...',
    'dash.autoResolutionRate': '자동 해결률',
    'dash.escalationRate': '에스컬레이션 비율',
    'dash.totalConversationsAnalyzed': '분석된 AI 대화 총계',
    'dash.noConversationData': '아직 AI 대화 데이터가 없습니다. 고객이 AI 채팅을 사용하면 지표가 표시됩니다.',
    'dash.systemHealth': '시스템 상태',
    'dash.businessHealth': '비즈니스 상태',
    'dash.healthy': '정상',
    'dash.degraded': '저하됨',
    'dash.unavailable': '사용 불가',
    'dash.someServicesDegraded': '일부 서비스가 저하되었을 수 있습니다',
    'dash.checkingServices': '서비스 확인 중…',
    'dash.notConfigured': '구성되지 않음',
    'dash.lastChecked': '마지막 확인:',
    'dash.loadingDashboard': '대시보드 불러오는 중...',
    'dash.loadingBusinessInfo': '비즈니스 정보를 불러오는 동안 기다려 주세요.',
    'dash.loadingDashboardWait': '대시보드를 불러오는 동안 기다려 주세요.',
    'dash.loadingBusinessData': '비즈니스 데이터 불러오는 중...',
    'dash.readOnly.title': '계정이 읽기 전용 모드입니다',
    'dash.readOnly.desc': '{label}을(를) 사용하려면 구독을 다시 활성화하세요.',
    'dash.readOnly.tooltip': '읽기 전용 모드 — 잠금 해제하려면 다시 활성화',
    'navdesc.dashboardOverview': '대시보드 개요',
    'navdesc.monitorAiVoice': 'AI 음성 상호작용 모니터링',
    'navdesc.testAiVoiceComplex': '복잡한 시나리오로 AI 음성 테스트',
    'navdesc.makeIntegratedCalls': '통합 통화 걸기',
    'navdesc.manageContacts': '연락처 관리',
    'navdesc.browseProducts': '제품 및 서비스 둘러보기',
    'navdesc.participateSurveys': '설문에 참여',
    'navdesc.collaborateTeam': '팀원과 협업',
    'navdesc.allInOneAgent': '올인원 상담원 인터페이스',
    'navdesc.manageCustomerTickets': '고객 티켓 관리',
    'navdesc.handleEscalated': '에스컬레이션된 대화 처리',
    'navdesc.viewSolutions': '솔루션 보기',
    'navdesc.aiVoice30': '30개 이상 시나리오의 AI 음성',
    'navdesc.aiChat30': '30개 이상 시나리오의 AI 채팅',
    'navdesc.customerManagement': '고객 관리',
    'navdesc.monitorTeamPerf': '팀 성과 모니터링',
    'navdesc.manageTeamRoles': '팀원 및 역할 관리',
    'navdesc.advancedTicketOps': '고급 티켓 작업',
    'navdesc.approveReject': '제출된 솔루션 승인 또는 거부',
    'navdesc.manageFeedbackCats': '피드백 카테고리 관리',
    'navdesc.reportsInsights': '보고서, 인사이트 및 AI 지표',
    'navdesc.manageShowroom': '비즈니스 쇼룸 관리',
    'navdesc.createSurveys': '고객 설문 만들기',
    'navdesc.unifiedDataAccess': '통합 데이터 액세스',
    'navdesc.aiPerfAnalytics': 'AI 성과 분석',
    'navdesc.advancedReporting': '고급 보고 및 AI 지표',
    'navdesc.teamResourceMgmt': '팀 및 리소스 관리',
    'navdesc.strategicInsights': '전략적 인사이트',
    'navdesc.strategicAnalytics': '전략 분석 및 AI 지표',
    'navdesc.cLevelInsights': '경영진 인사이트',
    'navdesc.revenueRoi': '매출 및 ROI 분석',
    'navdesc.executiveReporting': '경영진 보고 및 AI 지표',
    'navdesc.apiSystemMgmt': 'API 및 시스템 관리',
    'navdesc.perfSystemLogs': '성능 및 시스템 로그',
    'navdesc.systemAnalytics': '시스템 분석 및 AI 지표',
    'navdesc.systemConfig': '시스템 구성',
    'navdesc.manageTeamMembers': '팀원 관리',
    'navdesc.planInvoices': '요금제, 청구서, 업그레이드',
    'navdesc.allTicketAccess': '모든 티켓 액세스',
    'navdesc.knowledgeBase': '지식 베이스',
    'navdesc.customerOversight': '고객 관리 감독',
    'navdesc.fullAnalytics': '전체 분석 및 AI 지표',
  },
  zh: {
    'dash.greeting.admin': '系统概览与管理',
    'dash.greeting.operator': '客户服务仪表板',
    'dash.greeting.manager': '团队绩效概览',
    'dash.greeting.analyst': '数据分析仪表板',
    'dash.greeting.director': '战略洞察仪表板',
    'dash.greeting.executive': '高管摘要仪表板',
    'dash.greeting.it': '技术运营仪表板',
    'dash.greeting.super_admin': '全球控制中心',
    'dash.greeting.default': '仪表板概览',
    'dash.welcome': '欢迎回来，{name}！',
    'dash.brandId': '品牌 ID：',
    'dash.stat.totalTickets': '工单总数',
    'dash.stat.resolved': '已解决',
    'dash.stat.activeEscalations': '活动升级',
    'dash.stat.conversations': '对话',
    'dash.stat.teamMembers': '团队成员',
    'dash.callQueue.title': '来电队列',
    'dash.callQueue.subtitle': '{business} 的实时升级队列',
    'dash.callQueue.checking': '正在检查队列...',
    'dash.callQueue.live': '实时',
    'dash.callQueue.callsWaiting': '队列中 {count} 个',
    'dash.callQueue.customer': '客户：',
    'dash.callQueue.reason': '原因：',
    'dash.callQueue.status': '状态：',
    'dash.callQueue.dismiss': '忽略',
    'dash.callQueue.acceptCall': '接听',
    'dash.callQueue.empty': '队列中没有来电',
    'dash.callQueue.emptyDetail': '当前队列为空。',
    'dash.callQueue.openDesktop': '打开坐席工作台',
    'dash.newTicket.title': '创建新工单',
    'dash.newTicket.subtitle': '为客户创建支持工单',
    'dash.newTicket.customer': '客户 *',
    'dash.newTicket.customerPlaceholder': '客户姓名或电子邮件',
    'dash.newTicket.issueTitle': '问题标题 *',
    'dash.newTicket.issueTitlePlaceholder': '问题的简要描述',
    'dash.newTicket.category': '类别',
    'dash.newTicket.selectCategory': '选择类别',
    'dash.newTicket.catTechnical': '技术支持',
    'dash.newTicket.catBilling': '账单',
    'dash.newTicket.catGeneral': '一般咨询',
    'dash.newTicket.catAccount': '账户管理',
    'dash.newTicket.catProduct': '产品支持',
    'dash.newTicket.priority': '优先级',
    'dash.newTicket.priorityLow': '低',
    'dash.newTicket.priorityMedium': '中',
    'dash.newTicket.priorityHigh': '高',
    'dash.newTicket.priorityUrgent': '紧急',
    'dash.newTicket.description': '描述 *',
    'dash.newTicket.descriptionPlaceholder': '问题的详细描述',
    'dash.newTicket.create': '创建工单',
    'dash.toast.missingInfo': '信息缺失',
    'dash.toast.missingInfoDetail': '请填写所有必填字段',
    'dash.toast.ticketCreated': '工单已创建',
    'dash.toast.ticketCreatedDetail': '支持工单已成功创建',
    'dash.toast.ticketError': '创建工单失败。请重试。',
    'dash.sampleData': '示例数据',
    'dash.notConnectedShort': '尚未连接到实时数据源。',
    'dash.notConnectedLong': '此指标尚未连接到实时数据源。',
    'dash.live': '实时',
    'dash.recentActivity': '近期活动',
    'dash.loadingActivity': '正在加载活动...',
    'dash.noActivity': '暂无近期活动。',
    'dash.performanceMetrics': '性能指标',
    'dash.loadingMetrics': '正在加载指标...',
    'dash.autoResolutionRate': '自动解决率',
    'dash.escalationRate': '升级率',
    'dash.totalConversationsAnalyzed': '已分析的 AI 对话总数',
    'dash.noConversationData': '暂无 AI 对话数据。客户使用 AI 聊天后将显示指标。',
    'dash.systemHealth': '系统健康',
    'dash.businessHealth': '业务健康',
    'dash.healthy': '正常',
    'dash.degraded': '降级',
    'dash.unavailable': '不可用',
    'dash.someServicesDegraded': '部分服务可能已降级',
    'dash.checkingServices': '正在检查服务…',
    'dash.notConfigured': '未配置',
    'dash.lastChecked': '上次检查：',
    'dash.loadingDashboard': '正在加载仪表板...',
    'dash.loadingBusinessInfo': '正在加载您的企业信息，请稍候。',
    'dash.loadingDashboardWait': '正在加载您的仪表板，请稍候。',
    'dash.loadingBusinessData': '正在加载企业数据...',
    'dash.readOnly.title': '账户处于只读模式',
    'dash.readOnly.desc': '重新激活您的订阅以使用 {label}。',
    'dash.readOnly.tooltip': '只读模式 — 重新激活以解锁',
    'navdesc.dashboardOverview': '仪表板概览',
    'navdesc.monitorAiVoice': '监控 AI 语音交互',
    'navdesc.testAiVoiceComplex': '使用复杂场景测试 AI 语音',
    'navdesc.makeIntegratedCalls': '进行集成通话',
    'navdesc.manageContacts': '管理您的联系人',
    'navdesc.browseProducts': '浏览产品和服务',
    'navdesc.participateSurveys': '参与调查',
    'navdesc.collaborateTeam': '与团队成员协作',
    'navdesc.allInOneAgent': '一体化坐席界面',
    'navdesc.manageCustomerTickets': '管理客户工单',
    'navdesc.handleEscalated': '处理升级的对话',
    'navdesc.viewSolutions': '查看解决方案',
    'navdesc.aiVoice30': '包含 30 多个场景的 AI 语音',
    'navdesc.aiChat30': '包含 30 多个场景的 AI 聊天',
    'navdesc.customerManagement': '客户管理',
    'navdesc.monitorTeamPerf': '监控团队绩效',
    'navdesc.manageTeamRoles': '管理团队成员和角色',
    'navdesc.advancedTicketOps': '高级工单操作',
    'navdesc.approveReject': '批准或拒绝已提交的解决方案',
    'navdesc.manageFeedbackCats': '管理反馈类别',
    'navdesc.reportsInsights': '报告、洞察和 AI 指标',
    'navdesc.manageShowroom': '管理企业展厅',
    'navdesc.createSurveys': '创建客户调查',
    'navdesc.unifiedDataAccess': '统一数据访问',
    'navdesc.aiPerfAnalytics': 'AI 性能分析',
    'navdesc.advancedReporting': '高级报告和 AI 指标',
    'navdesc.teamResourceMgmt': '团队与资源管理',
    'navdesc.strategicInsights': '战略洞察',
    'navdesc.strategicAnalytics': '战略分析和 AI 指标',
    'navdesc.cLevelInsights': '高管洞察',
    'navdesc.revenueRoi': '收入与投资回报率分析',
    'navdesc.executiveReporting': '高管报告和 AI 指标',
    'navdesc.apiSystemMgmt': 'API 与系统管理',
    'navdesc.perfSystemLogs': '性能与系统日志',
    'navdesc.systemAnalytics': '系统分析和 AI 指标',
    'navdesc.systemConfig': '系统配置',
    'navdesc.manageTeamMembers': '管理团队成员',
    'navdesc.planInvoices': '套餐、发票、升级',
    'navdesc.allTicketAccess': '访问所有工单',
    'navdesc.knowledgeBase': '知识库',
    'navdesc.customerOversight': '客户监督',
    'navdesc.fullAnalytics': '完整分析和 AI 指标',
  },
  ar: {
    'dash.greeting.admin': 'نظرة عامة على النظام وإدارته',
    'dash.greeting.operator': 'لوحة خدمة العملاء',
    'dash.greeting.manager': 'نظرة عامة على أداء الفريق',
    'dash.greeting.analyst': 'لوحة تحليل البيانات',
    'dash.greeting.director': 'لوحة الرؤى الاستراتيجية',
    'dash.greeting.executive': 'لوحة الملخص التنفيذي',
    'dash.greeting.it': 'لوحة العمليات التقنية',
    'dash.greeting.super_admin': 'برج التحكم العالمي',
    'dash.greeting.default': 'نظرة عامة على اللوحة',
    'dash.welcome': 'مرحبًا بعودتك، {name}!',
    'dash.brandId': 'معرّف العلامة التجارية:',
    'dash.stat.totalTickets': 'إجمالي التذاكر',
    'dash.stat.resolved': 'تم الحل',
    'dash.stat.activeEscalations': 'التصعيدات النشطة',
    'dash.stat.conversations': 'المحادثات',
    'dash.stat.teamMembers': 'أعضاء الفريق',
    'dash.callQueue.title': 'قائمة انتظار المكالمات الواردة',
    'dash.callQueue.subtitle': 'قائمة انتظار التصعيد المباشرة لـ {business}',
    'dash.callQueue.checking': 'جارٍ التحقق من القائمة...',
    'dash.callQueue.live': 'مباشر',
    'dash.callQueue.callsWaiting': '{count} في القائمة',
    'dash.callQueue.customer': 'العميل:',
    'dash.callQueue.reason': 'السبب:',
    'dash.callQueue.status': 'الحالة:',
    'dash.callQueue.dismiss': 'تجاهل',
    'dash.callQueue.acceptCall': 'قبول المكالمة',
    'dash.callQueue.empty': 'لا توجد مكالمات واردة في القائمة',
    'dash.callQueue.emptyDetail': 'القائمة فارغة حاليًا.',
    'dash.callQueue.openDesktop': 'فتح سطح مكتب الوكيل',
    'dash.newTicket.title': 'إنشاء تذكرة جديدة',
    'dash.newTicket.subtitle': 'أنشئ تذكرة دعم لعميل',
    'dash.newTicket.customer': 'العميل *',
    'dash.newTicket.customerPlaceholder': 'اسم العميل أو بريده الإلكتروني',
    'dash.newTicket.issueTitle': 'عنوان المشكلة *',
    'dash.newTicket.issueTitlePlaceholder': 'وصف موجز للمشكلة',
    'dash.newTicket.category': 'الفئة',
    'dash.newTicket.selectCategory': 'اختر الفئة',
    'dash.newTicket.catTechnical': 'الدعم الفني',
    'dash.newTicket.catBilling': 'الفوترة',
    'dash.newTicket.catGeneral': 'استفسار عام',
    'dash.newTicket.catAccount': 'إدارة الحساب',
    'dash.newTicket.catProduct': 'دعم المنتج',
    'dash.newTicket.priority': 'الأولوية',
    'dash.newTicket.priorityLow': 'منخفضة',
    'dash.newTicket.priorityMedium': 'متوسطة',
    'dash.newTicket.priorityHigh': 'عالية',
    'dash.newTicket.priorityUrgent': 'عاجلة',
    'dash.newTicket.description': 'الوصف *',
    'dash.newTicket.descriptionPlaceholder': 'وصف تفصيلي للمشكلة',
    'dash.newTicket.create': 'إنشاء التذكرة',
    'dash.toast.missingInfo': 'معلومات ناقصة',
    'dash.toast.missingInfoDetail': 'يرجى ملء جميع الحقول المطلوبة',
    'dash.toast.ticketCreated': 'تم إنشاء التذكرة',
    'dash.toast.ticketCreatedDetail': 'تم إنشاء تذكرة الدعم بنجاح',
    'dash.toast.ticketError': 'تعذّر إنشاء التذكرة. يرجى المحاولة مرة أخرى.',
    'dash.sampleData': 'بيانات نموذجية',
    'dash.notConnectedShort': 'غير متصل بعد بمصدر مباشر.',
    'dash.notConnectedLong': 'هذا المقياس غير متصل بعد بمصدر بيانات مباشر.',
    'dash.live': 'مباشر',
    'dash.recentActivity': 'النشاط الأخير',
    'dash.loadingActivity': 'جارٍ تحميل النشاط...',
    'dash.noActivity': 'لا يوجد نشاط حديث بعد.',
    'dash.performanceMetrics': 'مقاييس الأداء',
    'dash.loadingMetrics': 'جارٍ تحميل المقاييس...',
    'dash.autoResolutionRate': 'معدل الحل التلقائي',
    'dash.escalationRate': 'معدل التصعيد',
    'dash.totalConversationsAnalyzed': 'إجمالي محادثات الذكاء الاصطناعي التي تم تحليلها',
    'dash.noConversationData': 'لا توجد بيانات محادثات الذكاء الاصطناعي بعد. ستظهر المقاييس عندما يستخدم العملاء دردشة الذكاء الاصطناعي.',
    'dash.systemHealth': 'صحة النظام',
    'dash.businessHealth': 'صحة النشاط التجاري',
    'dash.healthy': 'سليم',
    'dash.degraded': 'متدهور',
    'dash.unavailable': 'غير متاح',
    'dash.someServicesDegraded': 'قد تكون بعض الخدمات متدهورة',
    'dash.checkingServices': 'جارٍ التحقق من الخدمات…',
    'dash.notConfigured': 'غير مُهيأ',
    'dash.lastChecked': 'آخر فحص:',
    'dash.loadingDashboard': 'جارٍ تحميل اللوحة...',
    'dash.loadingBusinessInfo': 'يرجى الانتظار بينما نقوم بتحميل معلومات نشاطك التجاري.',
    'dash.loadingDashboardWait': 'يرجى الانتظار بينما نقوم بتحميل لوحتك.',
    'dash.loadingBusinessData': 'جارٍ تحميل بيانات النشاط التجاري...',
    'dash.readOnly.title': 'الحساب في وضع القراءة فقط',
    'dash.readOnly.desc': 'أعد تفعيل اشتراكك لاستخدام {label}.',
    'dash.readOnly.tooltip': 'وضع القراءة فقط — أعد التفعيل لإلغاء القفل',
    'navdesc.dashboardOverview': 'نظرة عامة على اللوحة',
    'navdesc.monitorAiVoice': 'مراقبة تفاعلات الصوت بالذكاء الاصطناعي',
    'navdesc.testAiVoiceComplex': 'اختبار الصوت بالذكاء الاصطناعي بسيناريوهات معقدة',
    'navdesc.makeIntegratedCalls': 'إجراء مكالمات متكاملة',
    'navdesc.manageContacts': 'إدارة جهات اتصالك',
    'navdesc.browseProducts': 'تصفح المنتجات والخدمات',
    'navdesc.participateSurveys': 'المشاركة في الاستطلاعات',
    'navdesc.collaborateTeam': 'التعاون مع أعضاء الفريق',
    'navdesc.allInOneAgent': 'واجهة وكيل شاملة',
    'navdesc.manageCustomerTickets': 'إدارة تذاكر العملاء',
    'navdesc.handleEscalated': 'معالجة المحادثات المصعّدة',
    'navdesc.viewSolutions': 'عرض الحلول',
    'navdesc.aiVoice30': 'صوت الذكاء الاصطناعي مع أكثر من 30 سيناريو',
    'navdesc.aiChat30': 'دردشة الذكاء الاصطناعي مع أكثر من 30 سيناريو',
    'navdesc.customerManagement': 'إدارة العملاء',
    'navdesc.monitorTeamPerf': 'مراقبة أداء الفريق',
    'navdesc.manageTeamRoles': 'إدارة أعضاء الفريق وأدوارهم',
    'navdesc.advancedTicketOps': 'عمليات متقدمة على التذاكر',
    'navdesc.approveReject': 'الموافقة على الحلول المقدمة أو رفضها',
    'navdesc.manageFeedbackCats': 'إدارة فئات الملاحظات',
    'navdesc.reportsInsights': 'التقارير والرؤى ومقاييس الذكاء الاصطناعي',
    'navdesc.manageShowroom': 'إدارة صالة عرض النشاط التجاري',
    'navdesc.createSurveys': 'إنشاء استطلاعات العملاء',
    'navdesc.unifiedDataAccess': 'وصول موحد إلى البيانات',
    'navdesc.aiPerfAnalytics': 'تحليلات أداء الذكاء الاصطناعي',
    'navdesc.advancedReporting': 'تقارير متقدمة ومقاييس الذكاء الاصطناعي',
    'navdesc.teamResourceMgmt': 'إدارة الفريق والموارد',
    'navdesc.strategicInsights': 'رؤى استراتيجية',
    'navdesc.strategicAnalytics': 'تحليلات استراتيجية ومقاييس الذكاء الاصطناعي',
    'navdesc.cLevelInsights': 'رؤى للإدارة العليا',
    'navdesc.revenueRoi': 'تحليل الإيرادات والعائد على الاستثمار',
    'navdesc.executiveReporting': 'التقارير التنفيذية ومقاييس الذكاء الاصطناعي',
    'navdesc.apiSystemMgmt': 'إدارة واجهات البرمجة والنظام',
    'navdesc.perfSystemLogs': 'الأداء وسجلات النظام',
    'navdesc.systemAnalytics': 'تحليلات النظام ومقاييس الذكاء الاصطناعي',
    'navdesc.systemConfig': 'تكوين النظام',
    'navdesc.manageTeamMembers': 'إدارة أعضاء الفريق',
    'navdesc.planInvoices': 'الخطة والفواتير والترقية',
    'navdesc.allTicketAccess': 'الوصول إلى جميع التذاكر',
    'navdesc.knowledgeBase': 'قاعدة المعرفة',
    'navdesc.customerOversight': 'الإشراف على العملاء',
    'navdesc.fullAnalytics': 'تحليلات كاملة ومقاييس الذكاء الاصطناعي',
  },
};

export default dashboardTranslations;
